import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import {
  ADMIN_INQUIRY_STATUS_LABEL,
  type AdminInquiryCounts,
  type AdminInquiryDetail,
  type AdminInquiryRow,
  type InquiryStatus,
} from '../api/inquiry'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminInquiryDetailModal from '../models/AdminInquiryDetailModal'

type Tab = '' | InquiryStatus

const TABS: { key: Tab; label: string; countKey: keyof AdminInquiryCounts }[] = [
  { key: '', label: '전체', countKey: 'all' },
  { key: 'open', label: '대기', countKey: 'open' },
  { key: 'answered', label: '답변함', countKey: 'answered' },
  { key: 'closed', label: '종료', countKey: 'closed' },
]

const statusChip = (status: InquiryStatus): string => {
  if (status === 'open') return 'bg-[#e08a3c]/15 text-[#c4742c]'
  if (status === 'answered') return 'bg-[#4a8a5c]/15 text-[#3b7049]'
  return 'bg-brandPrimary-10% text-textColorText3'
}

/**
 * 고객센터 문의 관리.
 *
 * 정렬은 서버가 한다 — 대기 → 답변함 → 종료, 그 안에서 마지막 쪽지가 최근인 것부터.
 * 사용자가 다시 쓰면 상태가 '대기'로 돌아가 자동으로 맨 위로 올라온다.
 */
const AdminInquiries: React.FC = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<AdminInquiryRow[]>([])
  const [counts, setCounts] = useState<AdminInquiryCounts>({
    all: 0,
    open: 0,
    answered: 0,
    closed: 0,
  })
  const [tab, setTab] = useState<Tab>('')
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)

  const [detail, setDetail] = useState<AdminInquiryDetail | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchRows = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await adminApi.getInquiries({
        page: currentPage,
        per_page: 20,
        status: tab,
        search: appliedSearch || undefined,
      })
      setRows(res.data)
      setCounts(res.counts)
      setLastPage(res.last_page)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('문의 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, tab, appliedSearch, navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchRows()
  }, [navigate, fetchRows])

  const openDetail = async (id: number) => {
    try {
      const res = await adminApi.getInquiry(id)
      setDetail(res)
      setIsModalOpen(true)
    } catch {
      toastUtil.error('문의를 불러오지 못했습니다')
    }
  }

  // 답변·상태 변경 뒤 상세와 목록을 모두 새로 받는다.
  // 상태 태그가 두 곳에 있어 한쪽만 갱신하면 어긋나 보인다.
  const handleChanged = async () => {
    if (detail) {
      try {
        setDetail(await adminApi.getInquiry(detail.inquiry.id))
      } catch {
        /* 목록이라도 맞춘다 */
      }
    }
    fetchRows()
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setDetail(null)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    setAppliedSearch(search.trim())
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">문의 관리</h2>
              <div className="text-style-body2 text-textColorText2">
                대기 {counts.open.toLocaleString()}건
              </div>
            </div>

            {/* 상태 탭 */}
            <div className="flex flex-wrap gap-8px mb-20px">
              {TABS.map((t) => {
                const isActive = tab === t.key
                return (
                  <button
                    key={t.key || 'all'}
                    type="button"
                    onClick={() => {
                      setTab(t.key)
                      setCurrentPage(1)
                    }}
                    className={`py-8px px-14px rounded-8px text-style-button2 border transition-colors ${
                      isActive
                        ? 'bg-4B3621-80%-10% border-transparent text-brown-80%-main'
                        : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                    }`}
                  >
                    {t.label} {counts[t.countKey].toLocaleString()}
                  </button>
                )
              })}
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="제목, 회원명, 쪽지 내용으로 검색"
                  className="flex-1 py-12px px-16px border border-brandPrimary-20% rounded-8px text-style-body1 text-textColorText2 bg-transparent focus:outline-none focus:border-brandPrimary placeholder:text-textColorText2"
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
              </div>
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">로딩 중...</div>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  문의가 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        {['제목', '회원', '분류', '상태', '쪽지', '버전', '마지막'].map(
                          (h) => (
                            <th
                              key={h}
                              className="text-left py-12px px-16px text-style-labelM text-textColorText2"
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => openDetail(row.id)}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors cursor-pointer"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2 max-w-[280px] truncate">
                            {row.subject}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {row.user
                              ? `${row.user.nickname} (${row.user.username})`
                              : '탈퇴'}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {row.category_label}
                          </td>
                          <td className="py-12px px-16px">
                            <span
                              className={`px-10px py-4px rounded-12px text-style-captionM ${statusChip(row.status)}`}
                            >
                              {ADMIN_INQUIRY_STATUS_LABEL[row.status]}
                            </span>
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {row.message_count}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {row.app_version || '-'}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2 whitespace-nowrap">
                            {row.updated_at}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-16px mt-24px">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`py-8px px-16px rounded-8px text-style-button2 border ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }`}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(lastPage, p + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`py-8px px-16px rounded-8px text-style-button2 border ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }`}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <AdminBaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={detail?.inquiry.subject || '문의'}
        maxWidth="max-w-[760px]"
        overlayOpacity="bg-opacity-50"
      >
        {detail && (
          <AdminInquiryDetailModal
            detail={detail}
            onChanged={handleChanged}
            onClose={handleCloseModal}
          />
        )}
      </AdminBaseModal>
    </div>
  )
}

export default AdminInquiries
